import { CircularProgress } from '@mui/material';
import * as React from 'react';
import type { Options } from 'react-youtube';
import Youtube from 'react-youtube';
import { makeStyles } from 'tss-react/mui';
import type { YouTubePlayer } from 'youtube-player/dist/types';
import { MAX_VIDEO_DURATION_IN_SECONDS } from '../../constants';
import useVideoInfo from '../../hooks/useVideoInfo';
import { PlatformType } from '../../utils/videoHelper';

const useStyles = makeStyles()(() => ({
  youtubeContainer: {
    position: 'relative',
    width: '100%',
    height: 0,
    paddingBottom: '56.25%',
    overflow: 'hidden',
    marginTop: 34,
    marginBottom: 34,
    '& iframe': {
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
    },
  },
  spinner: {
    margin: 35,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export interface VideoPreviewProps {
  videoUrl: string;
  onDurationTooLong?: (event?: { target?: any }) => void;
  onError?: (event?: { target?: any; data: number }) => void;
  onReady?: (event?: { target?: any }) => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({
  videoUrl,
  onDurationTooLong,
  onError,
  onReady,
}) => {
  const { classes } = useStyles();
  const { id, platform } = useVideoInfo(videoUrl);

  const options: Options = {
    playerVars: {
      controls: 1,
      rel: 0,
      fs: 0,
      enablejsapi: 1,
    },
  };

  const onHandleYoutubeReady = async (event: { target: YouTubePlayer }) => {
    const duration = event.target.getDuration() as unknown as number;
    if (duration > MAX_VIDEO_DURATION_IN_SECONDS) {
      if (onDurationTooLong) onDurationTooLong(event);
    } else if (onReady) onReady(event);
  };

  if (platform === PlatformType.youtube) {
    return (
      <div>
        <Youtube
          videoId={id}
          opts={options}
          containerClassName={classes.youtubeContainer}
          onError={onError}
          onReady={onHandleYoutubeReady}
        />
      </div>
    );
  }
  if (platform === PlatformType.tiktok) {
    return (
      <iframe
        src={`https://www.tiktok.com/player/v1/${id}?autoplay=0&controls=1&description=0&rel=0&native_context_menu=0&closed_caption=0`}
        width="300"
        height="500"
        title="TikTok Video"
        style={{ border: 'none' }}
      />
    );
  }
  if (platform === PlatformType.instagram) {
    return (
      <div>
        <iframe
          width="200"
          height="355"
          src={`https://www.instagram.com/p/${id}/embed`}
          title="Instagram Video"
        />
      </div>
    );
  }

  return (
    <div className={classes.spinner}>
      <CircularProgress size={40} color="primary" />
    </div>
  );
};

export default VideoPreview;
